import { router } from "@inertiajs/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";

import ButtonIcon from "@/components/Buttons/SimpleButton";
import Dropdown from "@/components/Dropdown";
import Footer from "@/components/Footer";
import Table from "@/components/Table";
import Typography from "@/components/Typography";
import { formatDateIgnoringTimezone, monthYearFormat } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";
import { moneyFormatterWithoutCurrency } from "@/utils/moneyFormat";

import {
  BreakDiv,
  ButtonDiv,
  ColumnDiv,
  DatesSecondDiv,
  FiltersContainer,
  FirstDiv,
  Line,
  SecondDiv,
  StartDiv,
} from "./styles";

const yearsOptions = ["Todos", ...getYearOptions()];

interface Debt {
  id: number;
  user_id: number;
  consumer_name: string;
  reference_month: string;
  due_date: string;
  price: number;
  days_late: number;
}

interface ManagerDebtsProps {
  debts: Debt[];
  debtsInfo: {
    total: number;
    count: number;
    consumers_count: number;
  };
}

const ManagerDebts = ({ debts, debtsInfo }: ManagerDebtsProps) => {
  const columnHelper = createColumnHelper<Debt>();
  const [year, setYear] = useState(
    new URLSearchParams(window.location.search).get("year") || "Todos",
  );

  const changeFilters = (selectedYear: string) => {
    setYear(selectedYear);
    router.reload({
      only: ["debts", "debtsInfo"],
      data: selectedYear === "Todos" ? {} : { year: selectedYear },
    });
  };

  const debt_columns = [
    columnHelper.accessor("consumer_name", {
      header: () => <div className="text-left">Cliente</div>,
      cell: (info) => (
        <div
          className="px-6 text-left"
          onClick={(e) => {
            e.stopPropagation();
            router.get(`/manager/internal-consumer/${info.row.original.user_id}`);
          }}
        >
          {info.row.original.consumer_name}
        </div>
      ),
    }),
    columnHelper.accessor("reference_month", {
      header: () => <div className="text-center">Mês de referência</div>,
      cell: (info) => (
        <div className="text-center">{monthYearFormat(info.row.original.reference_month)}</div>
      ),
    }),
    columnHelper.accessor("due_date", {
      header: () => "Vencimento",
      cell: (info) => formatDateIgnoringTimezone(info.row.original.due_date),
    }),
    columnHelper.accessor("price", {
      header: () => "Valor",
      cell: (info) => `R$ ${moneyFormatterWithoutCurrency(info.row.original.price)}`,
    }),
    columnHelper.accessor("days_late", {
      header: () => "Dias em atraso",
      cell: (info) => info.row.original.days_late,
    }),
  ];

  return (
    <>
      <FirstDiv>
        <ButtonDiv>
          <ButtonIcon
            onClick={() => router.get("/manager/home")}
            variant="simple"
            label="Voltar para dashboard"
          />
        </ButtonDiv>
        <StartDiv>
          <FiltersContainer>
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => changeFilters(value)}
              defaultValue={year}
            />
          </FiltersContainer>
          <div className="mt-4 flex w-full justify-between">
            <ColumnDiv>
              <Typography variant="overline" color="white">
                TODOS OS PERÍODOS
              </Typography>
              <Typography variant="h4" color="white">
                Dívidas
              </Typography>
            </ColumnDiv>
            <div className="col">
              <DatesSecondDiv>
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    VALOR TOTAL DAS DÍVIDAS
                  </Typography>
                  <div className="flex flex-row gap-2">
                    <Typography variant="pLarge" color="white">
                      R$
                    </Typography>
                    <Typography variant="pLarge" color="white">
                      {moneyFormatterWithoutCurrency(debtsInfo.total)}
                    </Typography>
                  </div>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    CONTAS EM DÍVIDA
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {debtsInfo.count}
                  </Typography>
                </BreakDiv>
                <Line />
                <BreakDiv>
                  <Typography variant="overline" color="white">
                    CLIENTES EM DÍVIDA
                  </Typography>
                  <Typography variant="pLarge" color="white">
                    {debtsInfo.consumers_count}
                  </Typography>
                </BreakDiv>
              </DatesSecondDiv>
            </div>
          </div>
        </StartDiv>
      </FirstDiv>
      <SecondDiv>
        <Typography variant="h4" color="black">
          Contas em atraso
        </Typography>
        <div className="mt-8">
          <Table data={debts} columns={debt_columns} onClickRow={null} />
        </div>
      </SecondDiv>
      <Footer />
    </>
  );
};

export default ManagerDebts;
